import React, { useEffect, useState } from "react";
import { Indent, Outdent, Trash2, Anchor, Diamond, Link2, ChevronRight, ChevronDown } from "lucide-react";
import { EditCell } from "@/components/EditCell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { COLUMN_BY_KEY, renderCell } from "@/lib/planColumns";

const LINK_TYPES = ["FS", "SS", "FF", "SF"];
export const TASK_STATUS = ["Not Started", "In Progress", "Complete", "Delayed", "On Hold"];

const wbsOf = (tasks, id) => tasks.find((t) => t.id === id)?.wbs || "?";

export const predLabel = (tasks, t) =>
  (t.predecessors || [])
    .map((p) => `${wbsOf(tasks, p.task_id)}${p.type}${p.lag_days ? (p.lag_days > 0 ? `+${p.lag_days}` : p.lag_days) : ""}`)
    .join(", ");

/** Predecessor editor for one task: pick tasks, link type and lag. */
export const LinkDialog = ({ task, tasks, onClose, onSave }) => {
  const [links, setLinks] = useState(task?.predecessors || []);
  useEffect(() => { setLinks(task?.predecessors ? [...task.predecessors] : []); }, [task]);
  if (!task) return null;
  const options = tasks.filter((t) => t.id !== task.id && !t.is_summary);
  const set = (i, key, value) => setLinks((l) => l.map((x, idx) => (idx === i ? { ...x, [key]: value } : x)));

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="link-dialog" className="max-h-[80vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Dependencies for “{task.name}”</DialogTitle>
          <DialogDescription>
            FS finish-to-start, SS start-to-start, FF finish-to-finish, SF start-to-finish. Lag is in
            working days and can be negative for a lead. Dates recalculate as soon as you save.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {links.length === 0 && <p className="rounded border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">No dependencies yet.</p>}
          {links.map((l, i) => (
            <div key={i} data-testid={`link-row-${i}`} className="grid grid-cols-12 items-end gap-2 rounded border border-slate-200 p-2">
              <div className="col-span-7">
                <label className="field-label">Predecessor</label>
                <select data-testid={`link-task-${i}`} value={l.task_id} onChange={(e) => set(i, "task_id", e.target.value)}
                  className="h-9 w-full rounded border border-slate-300 px-2 text-sm">
                  <option value="">Select task</option>
                  {options.map((o) => <option key={o.id} value={o.id}>{o.wbs} {o.name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="field-label">Type</label>
                <select data-testid={`link-type-${i}`} value={l.type || "FS"} onChange={(e) => set(i, "type", e.target.value)}
                  className="h-9 w-full rounded border border-slate-300 px-2 text-sm">
                  {LINK_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="field-label">Lag (days)</label>
                <Input data-testid={`link-lag-${i}`} type="number" value={l.lag_days ?? 0} onChange={(e) => set(i, "lag_days", Number(e.target.value) || 0)} className="h-9" />
              </div>
              <div className="col-span-1 pb-2">
                <button data-testid={`remove-link-${i}`} onClick={() => setLinks(links.filter((_, idx) => idx !== i))} aria-label="Remove link" className="text-slate-300 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          <Button size="sm" variant="outline" data-testid="add-link-btn" onClick={() => setLinks([...links, { task_id: "", type: "FS", lag_days: 0 }])} className="gap-1.5">
            <Link2 size={13} /> Add dependency
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="link-cancel-btn" onClick={onClose}>Cancel</Button>
          <Button data-testid="link-save-btn" onClick={() => onSave(links.filter((l) => l.task_id))} style={{ backgroundColor: "var(--brand-primary)" }}>
            Save dependencies
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** MS Project style outline grid, driven by the user's chosen columns. */
export const PlanOutline = ({ tasks, stageOptions, columns, currency = "£", onPatch, onOutline, onDelete, showCritical = true }) => {
  const [linking, setLinking] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const cols = columns.map((k) => COLUMN_BY_KEY[k]).filter(Boolean);

  const hasChildren = (t) => tasks.some((x) => x.parent_id === t.id);
  const hiddenIds = new Set();
  tasks.forEach((t) => {
    if (!collapsed[t.id]) return;
    const walk = (pid) => tasks.filter((x) => x.parent_id === pid).forEach((c) => { hiddenIds.add(c.id); walk(c.id); });
    walk(t.id);
  });
  const visible = tasks.filter((t) => !hiddenIds.has(t.id));
  const helpers = {
    patch: onPatch, EditCell, stageOptions, currency, taskStatus: TASK_STATUS,
    onLinks: setLinking, predLabel: (t) => predLabel(tasks, t),
  };

  return (
    <div className="overflow-x-auto" data-testid="plan-outline">
      <table className="w-full text-sm" style={{ minWidth: `${420 + cols.length * 96}px` }}>
        <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="whitespace-nowrap px-2 py-2 font-semibold">Task name</th>
            {cols.map((c) => <th key={c.key} className={`whitespace-nowrap px-2 py-2 font-semibold ${c.width || ""}`}>{c.label}</th>)}
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {visible.map((t) => {
            const level = Math.max((t.outline_level || 1) - 1, 0);
            const critical = showCritical && t.is_critical;
            return (
              <tr key={t.id} data-testid={`outline-row-${t.id}`}
                className={`group transition-colors hover:bg-slate-50/80 ${t.is_summary ? "bg-slate-50/50" : ""}`}>
                <td className="sticky left-0 z-10 min-w-[260px] bg-white px-2 py-1.5 group-hover:bg-slate-50/80">
                  <div className="flex items-center gap-1" style={{ paddingLeft: `${level * 16}px` }}>
                    {hasChildren(t) ? (
                      <button data-testid={`collapse-${t.id}`} onClick={() => setCollapsed({ ...collapsed, [t.id]: !collapsed[t.id] })}
                        className="text-slate-400 hover:text-slate-700">
                        {collapsed[t.id] ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                      </button>
                    ) : (
                      <button data-testid={`toggle-milestone-${t.id}`} onClick={() => onPatch(t, { is_milestone: !t.is_milestone })}
                        title={t.is_milestone ? "Make this a task" : "Make this a milestone"}
                        className={t.is_milestone ? "text-amber-500" : "text-slate-200 hover:text-slate-400"}>
                        <Diamond size={11} fill={t.is_milestone ? "currentColor" : "none"} />
                      </button>
                    )}
                    <EditCell testId={`cell-name-${t.id}`} value={t.name} onCommit={(v) => onPatch(t, { name: v })}
                      className={t.is_summary ? "font-semibold text-slate-900" : "font-medium text-slate-800"} placeholder="Untitled task" />
                    {critical && <span data-testid={`critical-flag-${t.id}`} title="On the critical path" className="ml-1 shrink-0 rounded bg-red-50 px-1 text-[9px] font-bold uppercase text-red-600">crit</span>}
                  </div>
                </td>
                {cols.map((c) => (
                  <td key={c.key} className={`whitespace-nowrap px-2 py-1.5 ${c.width || ""}`}>{renderCell(c, t, helpers)}</td>
                ))}
                <td className="px-2 py-1.5">
                  <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button data-testid={`outdent-${t.id}`} onClick={() => onOutline([t.id], "outdent")} aria-label="Outdent" title="Outdent" className="text-slate-400 hover:text-slate-800"><Outdent size={13} /></button>
                    <button data-testid={`indent-${t.id}`} onClick={() => onOutline([t.id], "indent")} aria-label="Indent" title="Indent" className="text-slate-400 hover:text-slate-800"><Indent size={13} /></button>
                    <button data-testid={`rebaseline-task-${t.id}`} onClick={() => onPatch(t, {}, true)} aria-label="Re-baseline" title="Re-baseline to current dates" className="text-slate-400 hover:text-indigo-600"><Anchor size={13} /></button>
                    <button data-testid={`delete-task-${t.id}`} onClick={() => onDelete(t)} aria-label="Delete task" title="Delete task" className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <LinkDialog task={linking} tasks={tasks} onClose={() => setLinking(null)}
        onSave={(links) => { onPatch(linking, { predecessors: links, constraint_mode: "auto" }); setLinking(null); }} />
    </div>
  );
};
